import { useState, useEffect, useRef } from 'react';
import { Sparkles, X, ChevronDown, CheckCircle, Database, Lightbulb, ShieldAlert } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';

/*
 * 元数据驱动的 AI 引导（方案）：
 * 引导不来自手工写死的 if/else 映射，而来自【字段元数据 + 已填值】。
 * - 字段元数据：字段名/类型/是否必填/取值范围/默认值/帮助文案/字段性质(事实型/经验型)
 * - 三类引导：
 *    validate（校验型，确定性）：必填未填 / 超范围 / 格式错
 *    explain （解释型）：基于字段元数据用大白话解释
 *    recommend（推荐型）：仅对【经验型】参数推荐值；【事实型】字段只解释不代填
 * 真实实现由 LLM 读取表单 schema + 已填值动态生成；此处用元数据演示同一逻辑。
 */
const FIELD_META = [
  { key: 'agentInfo.name', label: '智能体名称', nature: 'fact', required: true,
    help: '展示名称，必填' },
  { key: 'agentInfo.description', label: '能力描述', nature: 'fact', required: false,
    help: '用于超级智能体语义路由匹配，建议涵盖核心实体（如IP承载网）与操作意图（如诊断、查询），越精准路由越准' },
  { key: 'apiConfig.apiUrl', label: 'API 地址', nature: 'fact', required: true,
    help: '智能体后端服务端点，事实型字段，AI 无法代填' },
  { key: 'apiConfig.apiKey', label: 'API 密钥', nature: 'fact', required: true,
    help: '认证密钥，事实型敏感字段，需您从服务方获取，AI 不代填' },
  { key: 'apiConfig.truncation.tokenLimit', label: '会话截断 Token 阈值', nature: 'experience', required: false,
    range: '2048~32768', recommend: '8192', help: '经验型参数：主流模型在保持高性能时的黄金区间约 8192' },
];

// 取嵌套值
function getVal(config, key) {
  const map = {
    'agentInfo.name': config.agentInfo.name,
    'agentInfo.description': config.agentInfo.description,
    'apiConfig.apiUrl': config.apiConfig.apiUrl,
    'apiConfig.apiKey': config.apiConfig.apiKey,
    'apiConfig.truncation.tokenLimit': config.apiConfig.truncationStrategy?.tokenLimit,
  };
  return map[key];
}

function deriveHints(config) {
  const hints = [];
  for (const f of FIELD_META) {
    const val = getVal(config, f.key);
    // 密钥仅当选 API_KEY 认证时才校验
    if (f.key === 'apiConfig.apiKey' && config.apiConfig.authType !== 'API_KEY') continue;

    if (f.required && (!val || String(val).trim() === '')) {
      hints.push({ id: f.key + ':v', kind: 'validate', nature: f.nature, field: f.label,
        text: `「${f.label}」是必填项，尚未填写。${f.nature === 'fact' ? '事实型字段需您填写，AI 不代填。' : ''}` });
    } else if (!f.required && f.nature === 'fact' && (!val || String(val).trim() === '')) {
      hints.push({ id: f.key + ':e', kind: 'explain', nature: f.nature, field: f.label,
        text: `「${f.label}」：${f.help}` });
    }
    // 经验型参数：给推荐值
    if (f.nature === 'experience') {
      hints.push({ id: f.key + ':r', kind: 'recommend', nature: f.nature, field: f.label,
        text: `「${f.label}」${f.help}`, range: f.range, recommend: f.recommend, current: val });
    }
  }
  // 完成度
  const nameOk = !!config.agentInfo.name.trim();
  const urlOk = !!config.apiConfig.apiUrl.trim();
  const keyOk = config.apiConfig.authType !== 'API_KEY' || !!config.apiConfig.apiKey.trim();
  if (nameOk && urlOk && keyOk && config.agentInfo.description.trim()) {
    hints.push({ id: 'ready', kind: 'ready', text: '必填与关键字段已完善，可进行接口测试并保存上架。' });
  }
  return hints;
}

const KIND_META = {
  validate: { label: '校验', icon: ShieldAlert, cls: 'bg-red-50 border-red-200 text-red-700', dot: 'text-red-500' },
  explain: { label: '解释', icon: Database, cls: 'bg-blue-50 border-blue-200 text-blue-700', dot: 'text-blue-500' },
  recommend: { label: '推荐', icon: Lightbulb, cls: 'bg-amber-50 border-amber-200 text-amber-700', dot: 'text-amber-500' },
  ready: { label: '就绪', icon: CheckCircle, cls: 'bg-green-50 border-green-200 text-green-700', dot: 'text-green-500' },
};

export default function AiGuideAssistant() {
  const { config, updateApiConfig } = useConfig();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const [adopted, setAdopted] = useState({});
  const bottomRef = useRef(null);

  const hints = deriveHints(config);
  const sig = hints.map(h => h.id).join(',');

  useEffect(() => { if (!open) setHasNew(true); }, [sig]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [hints.length]);

  const errorCount = hints.filter(h => h.kind === 'validate').length;
  const isReady = hints.some(h => h.kind === 'ready');

  const adopt = (h) => {
    // 经验型参数一键采纳推荐值（仅演示截断阈值）
    if (h.field === '会话截断 Token 阈值') {
      updateApiConfig({ truncationStrategy: { ...config.apiConfig.truncationStrategy, tokenLimit: Number(h.recommend) } });
      setAdopted(prev => ({ ...prev, [h.id]: true }));
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
      {open && !minimized && (
        <div className="w-[22rem] bg-white border border-gray-200 rounded-2xl shadow-xl flex flex-col overflow-hidden" style={{ maxHeight: '460px' }}>
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600">
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-white" /><span className="text-sm font-semibold text-white">ECHO 配置助手</span></div>
            <div className="flex items-center gap-1">
              <button onClick={() => setMinimized(true)} className="p-1 hover:bg-white/10 rounded text-white"><ChevronDown className="w-4 h-4" /></button>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded text-white"><X className="w-4 h-4" /></button>
            </div>
          </div>

          {/* 方案说明 */}
          <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-100">
            <p className="text-xs text-indigo-600 leading-relaxed">基于<strong>字段元数据 + 已填值</strong>动态生成引导（非手工配规则）。事实型字段只解释、经验型参数可推荐值。</p>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
            {hints.length === 0 && <div className="text-xs text-gray-400 text-center py-6">开始填写表单，引导将实时出现</div>}
            {hints.map(h => {
              const m = KIND_META[h.kind];
              const Icon = m.icon;
              return (
                <div key={h.id} className={`text-xs leading-relaxed p-2.5 rounded-lg border ${m.cls}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon className={`w-3.5 h-3.5 ${m.dot}`} />
                    <span className="font-medium">{m.label}</span>
                    {h.nature && <span className="px-1.5 py-0.5 rounded bg-white/60 text-[10px]">{h.nature === 'fact' ? '事实型' : '经验型'}</span>}
                  </div>
                  <div>{h.text}</div>
                  {h.kind === 'recommend' && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-[11px] opacity-70">范围 {h.range}｜推荐 <strong>{h.recommend}</strong>｜当前 {h.current ?? '未设置'}</span>
                      {adopted[h.id] ? (
                        <span className="text-[11px] text-green-600 flex items-center gap-0.5"><CheckCircle className="w-3 h-3" /> 已采纳</span>
                      ) : (
                        <button onClick={() => adopt(h)} className="text-[11px] px-2 py-0.5 bg-amber-500 text-white rounded hover:bg-amber-600">采纳推荐值</button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          <div className="px-4 py-3 border-t border-gray-100 bg-white">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">配置状态</span>
              {isReady ? <span className="flex items-center gap-1 text-green-600 font-medium"><CheckCircle className="w-3 h-3" /> 可保存上架</span>
                : errorCount > 0 ? <span className="text-red-500 font-medium">{errorCount} 项待完善</span>
                : <span className="text-amber-500 font-medium">建议完善</span>}
            </div>
          </div>
        </div>
      )}

      <button onClick={() => { setOpen(true); setMinimized(false); setHasNew(false); }}
        className={`relative w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105 ${
          isReady ? 'bg-gradient-to-br from-green-500 to-emerald-600' : errorCount > 0 ? 'bg-gradient-to-br from-red-500 to-orange-500' : 'bg-gradient-to-br from-blue-600 to-indigo-600'}`}>
        <Sparkles className="w-5 h-5 text-white" />
        {hasNew && !open && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">!</span>}
      </button>
    </div>
  );
}
